import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SortIcon } from '@/components/shared/reports/SortIcon';
import type { PostingLogTimesheet, TimesheetSortKey, SortDir } from '@/utils/PostingLog/types';
import { formatDate } from '@/utils/PostingLog/formatters';
import { POSTING_DEFAULT_COLUMNS } from '@/utils/PostingLog/constants';
import { StatusBadge } from './StatusBadge';

interface TimesheetTableProps {
  timesheets: PostingLogTimesheet[];
  onTimesheetClick: (timesheetId: string) => void;
  sortKey: TimesheetSortKey;
  sortDir: SortDir;
  onSort: (key: TimesheetSortKey) => void;
  columnVisibility?: Record<string, boolean>;
}

export const TimesheetTable: React.FC<TimesheetTableProps> = ({
  timesheets, onTimesheetClick, sortKey, sortDir, onSort, columnVisibility,
}) => {
  const columns = POSTING_DEFAULT_COLUMNS.filter((column) => columnVisibility?.[column.id] !== false);
  const renderCell = (timesheet: PostingLogTimesheet, columnId: string) => {
    switch (columnId) {
      case 'timesheetId':
        return (
          <button
            type="button"
            className="text-primary underline-offset-2 hover:underline font-medium"
            onClick={() => onTimesheetClick(timesheet.timesheetId)}
          >
            {timesheet.timesheetId}
          </button>
        );
      case 'employeeName':
        return timesheet.employeeName;
      case 'weekEnding':
        return formatDate(timesheet.weekEnding);
      case 'erpRef':
        if (timesheet.erpRefs.length > 1) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs font-mono text-muted-foreground cursor-help">{timesheet.erpRef}</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <ul className="font-mono text-xs space-y-0.5">
                  {timesheet.erpRefs.map((ref) => <li key={ref}>{ref}</li>)}
                </ul>
              </TooltipContent>
            </Tooltip>
          );
        }
        return timesheet.erpRef
          ? <span className="text-xs font-mono text-muted-foreground">{timesheet.erpRef}</span>
          : <span className="text-muted-foreground opacity-40">—</span>;
      case 'status':
        return <StatusBadge status={timesheet.status} />;
    }
  };

  return (
    <TooltipProvider>
      <div className="rounded-md border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead
                    key={column.id}
                    className="whitespace-nowrap"
                    aria-sort={sortKey === column.id ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button type="button" onClick={() => onSort(column.id as TimesheetSortKey)}>
                      {column.label}
                      <SortIcon col={column.id} current={sortKey} dir={sortDir} />
                    </button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {timesheets.map((timesheet) => (
                <TableRow key={timesheet.timesheetId}>
                  {columns.map((column) => (
                    <TableCell key={column.id} className="text-sm whitespace-nowrap">
                      {renderCell(timesheet, column.id)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </TooltipProvider>
  );
};
